import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useCurrentPageType } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { useScmpWidgetConfig } from "scmp-app/lib/app/hooks";
import type { seriesWidgetTabContentArticle$key } from "scmp-app/queries/__generated__/seriesWidgetTabContentArticle.graphql";

import {
  ActionBar,
  Container,
  Headline,
  LeadingImage,
  Primary,
  Rest,
  SCMPSeriesLink,
  Series,
  SeriesLink,
  StyledContentCoverImage,
  StyledContentItemHomeSecondary,
  StyledEntityLink,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  preferTopicLabel?: boolean;
  reference: seriesWidgetTabContentArticle$key;
  withBookmark?: boolean;
  withComment?: boolean;
  withVideoOverlay?: boolean;
};

const Component = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, topicLinkVariant, withVideoOverlay = false }, reference) => {
    const content = useFragment(
      graphql`
        fragment seriesWidgetTabContentArticle on Article {
          seriesShortLabel
          entityId
          urlAlias
          ...entityLink
          series {
            ... on Content {
              entityId
              urlAlias
              publishedDate
              ...entityLink
              ...contentCoverImageContent
                @arguments(withLandscape250x99: true, withSize540x360: true)
              ...helpersCheckIsPlusArticle
              ...helpersCheckHasLiveTagArticle
              ...helpersUseContentSponsorContent
              ...hooksContentItemProvidersContent
                @arguments(
                  withSections: true
                  withTopicLink: true
                  withLiveTag: true
                  withHeadline: true
                  withSummary: true
                  withSubHeadline: true
                  withActionBar: true
                  withSponsorTag: true
                  withVideoDurationOverlay: true
                )
              ...homeSecondaryContentItemContent
            }
          }
        }
      `,
      reference_,
    );

    const [head, ...series] = [...(content?.series ?? [])].sort((a, b) => {
      const dateA = new Date(a?.publishedDate ?? 0).getTime();
      const dateB = new Date(b?.publishedDate ?? 0).getTime();
      return dateB - dateA;
    });
    const { scmpWidgetConfigState } = useScmpWidgetConfig();
    const providers = useContentItemProviders(head!, topicLinkVariant);
    const currentPageType = useCurrentPageType();
    if (!content || !content.seriesShortLabel || !head) {
      return null;
    }

    const isReadingTimeEnabled =
      scmpWidgetConfigState?.result?.homepage_widgets_reading_time?.series_widget === "1";

    const label = content.seriesShortLabel?.replace(/ /g, "_").toLowerCase() || "";

    const handleClick = (entityId?: string, destinationUrl?: string) => {
      sendGA4Tracking({
        action: "click",
        category: "recirculation",
        customized_parameters: {
          article_id: entityId,
          block_section: label,
          destination_url: destinationUrl,
          page_type: "homepage",
          widget_name: "scmp_series",
        },
        subcategory: "widget",
      });
    };

    const contentSponsor = getContentSponsor(head);
    const isSponsorArticle = notEmpty(contentSponsor?.name);
    const query = {
      module: `series_${label}`,
    };

    return (
      <BaseLinkContextProvider customQueryParameters={query}>
        <Container className={className} ref={reference}>
          <LeadingImage>
            <StyledEntityLink
              onClick={() => handleClick(head.entityId, head.urlAlias)}
              reference={head}
            >
              <StyledContentCoverImage
                reference={head}
                responsiveVariants={{
                  desktopUp: "landscape250x99",
                  mobileUp: "size540x360",
                  tabletUp: "landscape250x99",
                }}
              />
              {withVideoOverlay &&
                providers.video?.durationOverlay({
                  iconSize: "large",
                  variant: "homepage",
                })}
            </StyledEntityLink>
          </LeadingImage>
          <Series>
            <SCMPSeriesLink as="/series" pathname="/series">
              SCMP Series
            </SCMPSeriesLink>
            <SeriesLink
              onClick={() => handleClick(content.entityId, content.urlAlias)}
              reference={content}
            >
              {content.seriesShortLabel}
            </SeriesLink>
          </Series>
          <Primary>
            <StyledEntityLink
              onClick={() => handleClick(head.entityId, head.urlAlias)}
              reference={head}
            >
              <LinkHighlightArea>
                <Headline>
                  {providers.headline({
                    hideTooltip: true,
                    preferSocialHeadline: false,
                  })}
                </Headline>
                <Summary>
                  {providers.summary({
                    isTextOverflowEllipsis: true,
                  })}
                </Summary>
              </LinkHighlightArea>
            </StyledEntityLink>
            {isSponsorArticle ? (
              providers.sponsorTag?.({ variant: "home" })
            ) : (
              <ActionBar>
                {providers.actionBar({
                  commentVariant: "reverse",
                  displayDateVariant: isReadingTimeEnabled ? "readingTime" : "updatedDate",
                  isBookmarkVisible: false,
                  isCommentsVisible: false,
                })}
              </ActionBar>
            )}
          </Primary>
          <Rest>
            {series?.slice(0, 2)?.map(
              (node, index) =>
                node && (
                  <StyledContentItemHomeSecondary
                    customQueryParameters={{
                      pgtype: currentPageType,
                    }}
                    isReadingTimeEnabled={isReadingTimeEnabled}
                    key={index}
                    onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
                    reference={node}
                    skipHighlight={true}
                    withVideoPlayIcon
                  />
                ),
            )}
          </Rest>
        </Container>
      </BaseLinkContextProvider>
    );
  },
);

Component.displayName = "SeriesWidgetTabContent";

export const SeriesWidgetTabContent = withHighlightedHeadline(Component);
